import { useCallback, useEffect, useState, useRef } from "react";
import axios, { AxiosRequestConfig } from "axios";
import { Auth, signOut } from "firebase/auth";

import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, APIHandler } from "../../../api/base";
import { logout } from "../../../api/auth/endpoints-referrers";
import { TokenResponse } from "../../interfaces/auth/token-response.interface";
import { stringGetter } from "../../utilities/localstorage-dealer/localstorage-getters.util";
import {
    setDataToLocalStorage,
    removeDataToLocalStorage
} from "../../utilities/localstorage-dealer/localstorage-setter.util";

interface RetryQueueItem {
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
    config: AxiosRequestConfig;
}

/**
 * Recognize 401s that a fresh Firebase token would actually fix. Anything
 * else (user-level errors like "User not found in this tenant") must be
 * propagated as-is — refreshing the token can't change the answer, and
 * refreshing in a loop trips Firebase's per-project token-refresh quota,
 * which then locks out every other user of the same project for hours.
 *
 * Backend 401 shapes:
 *   - Firebase token expired / invalid signature / revoked → `Token validation failed: ...`
 *     (from FirebaseStrategy.validate) — refreshable
 *   - User row missing for the token's email → `User not found. Email: ...`
 *     (from UserPermissionsService.getFirebaseUser) — NOT refreshable, surface to caller
 *   - Embed session guard rejection → no body — refreshable (treated as generic)
 */
function isRefreshableAuthFailure(error: any): boolean {
    const msg = String(error?.response?.data?.message ?? "").toLowerCase();
    if (!msg) return true; // empty body = unknown; safest is to try a refresh
    if (msg.includes("user not found")) return false;
    if (msg.includes("no user record")) return false;
    return true;
}

// Axios marks the request config object as "already retried after a token
// refresh" so a second 401 on the same request can't kick off another
// refresh-retry cycle. Without this, a deterministic backend 401 (e.g. a
// case-mismatched user lookup) loops indefinitely and exhausts the Firebase
// token-refresh quota.
const REFRESH_RETRY_FLAG = "__mbassistRefreshRetried";

// Backend tenant auth policy refusals (403 with `code`), plan §2.2: none
// of them is fixable by a token refresh, all of them end the session.
const POLICY_REFUSAL_CODES = [
    "method_not_allowed",
    "support_session_expired",
    "domain_not_allowed",
    "directory_not_allowed"
];

/** Phase 3: the session is fine, the password is just too old. The user keeps
 *  their session and is sent to set a new password instead of being logged out. */
const PASSWORD_EXPIRED_CODE = "password_expired";
export const CHANGE_PASSWORD_PATH = "/change-password";

// Firebase REST API for token refresh (works without client SDK session)
const FIREBASE_TOKEN_REFRESH_URL = "https://securetoken.googleapis.com/v1/token";

/**
 * Refresh Firebase token using REST API
 * This works even when auth.currentUser is null (e.g., on tenant domain after auth domain login)
 */
async function refreshTokenViaRestApi(
    refreshToken: string,
    apiKey: string
): Promise<{ accessToken: string; refreshToken: string }> {
    const response = await axios.post(
        `${FIREBASE_TOKEN_REFRESH_URL}?key=${apiKey}`,
        new URLSearchParams({
            grant_type: "refresh_token",
            refresh_token: refreshToken
        }),
        {
            headers: {
                "Content-Type": "application/x-www-form-urlencoded"
            }
        }
    );

    return {
        accessToken: response.data.id_token,
        refreshToken: response.data.refresh_token
    };
}

export function useFirebaseToken(auth: Auth, onTokenInvalid: () => void) {
    const [currentAccessToken, setCurrentAccessToken] = useState<string>();
    // Reactive authentication state that components can depend on
    const [isAuthenticatedState, setIsAuthenticatedState] = useState<boolean>(() => {
        return !!stringGetter(ACCESS_TOKEN_KEY) && !!stringGetter(REFRESH_TOKEN_KEY);
    });

    // Use ref to track if interceptor is already set up (prevents duplicates on re-renders)
    const interceptorIdRef = useRef<number | null>(null);

    const getAccessToken = useCallback(() => {
        return stringGetter(ACCESS_TOKEN_KEY);
    }, []);

    const getRefreshToken = useCallback(() => {
        return stringGetter(REFRESH_TOKEN_KEY);
    }, []);

    const isAuthenticated = useCallback(() => {
        return !!getAccessToken() && !!getRefreshToken();
    }, [getAccessToken, getRefreshToken]);

    const setAccessToken = useCallback((token: string) => {
        setDataToLocalStorage(ACCESS_TOKEN_KEY, token);
        setCurrentAccessToken(token);
        setIsAuthenticatedState(true);
    }, []);

    const setTokens = useCallback(({ accessToken, refreshToken }: TokenResponse) => {
        setDataToLocalStorage(ACCESS_TOKEN_KEY, accessToken);
        setDataToLocalStorage(REFRESH_TOKEN_KEY, refreshToken);
        setCurrentAccessToken(accessToken);
        setIsAuthenticatedState(true);
    }, []);

    const clearToken = useCallback(
        async (shouldClearSession = false) => {
            const clearSession =
                shouldClearSession && isAuthenticated() ? logout() : Promise.resolve();
            try {
                return await clearSession;
            } finally {
                removeDataToLocalStorage(ACCESS_TOKEN_KEY);
                removeDataToLocalStorage(REFRESH_TOKEN_KEY);
                setCurrentAccessToken("");
                setIsAuthenticatedState(false);
                onTokenInvalid();
            }
        },
        [onTokenInvalid, isAuthenticated]
    );

    const updateCurrentAccessToken = useCallback(() => {
        setCurrentAccessToken(getAccessToken() ?? undefined);
        setIsAuthenticatedState(isAuthenticated());
    }, [getAccessToken, isAuthenticated]);

    useEffect(() => {
        updateCurrentAccessToken();
    }, [updateCurrentAccessToken]);

    useEffect(() => {
        // If interceptor already exists, don't add another
        if (interceptorIdRef.current !== null) {
            return;
        }

        const retryQueueList: RetryQueueItem[] = [];
        let isRefreshing = false;
        // Several in-flight requests fail together on a policy refusal; only
        // the first one tears the session down.
        let policyHandled = false;

        const interceptorId = APIHandler.interceptors.response.use(
            response => response,
            async error => {
                // Tenant auth policy refusal (tenant_authentication_plan.md §2.2):
                // the token is valid but its sign-in method is no longer allowed,
                // or a support session has run out. No refresh can fix either —
                // drop the session so the user lands on the login page, which
                // only offers what the tenant still allows.
                //
                // Local clear only: the backend refuses this token on EVERY
                // guarded route, including POST /auth/logout, so a server-side
                // logout here would 403 straight back into this branch and loop.
                // Sign the Firebase SDK out as well so nothing can re-mint.
                const policyCode = error.response?.data?.code;
                if (error.response?.status === 403 && policyCode === PASSWORD_EXPIRED_CODE) {
                    if (!window.location.pathname.startsWith(CHANGE_PASSWORD_PATH)) {
                        console.warn(
                            "[useFirebaseToken] Password expired; routing to change password"
                        );
                        window.location.assign(`${CHANGE_PASSWORD_PATH}?reason=expired`);
                    }
                    return Promise.reject(error);
                }
                if (error.response?.status === 403 && POLICY_REFUSAL_CODES.includes(policyCode)) {
                    // Only the CURRENT session gets torn down. A response
                    // for a token that has since been cleared or replaced
                    // (e.g. a refused session's last request landing while a
                    // support session signs in) must not touch the new one.
                    const sent = String(error.config?.headers?.Authorization ?? "");
                    const current = getAccessToken();
                    if (!current || sent !== `Bearer ${current}`) {
                        return Promise.reject(error);
                    }
                    if (policyHandled) {
                        return Promise.reject(error);
                    }
                    policyHandled = true;
                    console.warn("[useFirebaseToken] Tenant auth policy refused:", policyCode);
                    try {
                        await signOut(auth);
                    } catch (signOutError) {
                        console.warn("[useFirebaseToken] Firebase signOut failed:", signOutError);
                    }
                    try {
                        await clearToken(false);
                    } finally {
                        policyHandled = false;
                    }
                    return Promise.reject(error);
                }

                // Only handle 401 errors
                if (!error.response || error.response.status !== 401) {
                    return Promise.reject(error);
                }

                // Discriminate user-level 401 from token-level 401. A user-level
                // 401 (e.g. "User not found") will NOT be fixed by a fresh
                // Firebase token — refreshing in a loop here is what triggered
                // the QUOTA_EXCEEDED storm during the v2.0.9 RSD19901990 incident.
                if (!isRefreshableAuthFailure(error)) {
                    return Promise.reject(error);
                }

                // Idempotency: if THIS request has already been retried after a
                // refresh and still got 401, the underlying problem isn't a
                // stale token. Don't refresh again — propagate to the caller.
                const originalRequest: AxiosRequestConfig & {
                    [REFRESH_RETRY_FLAG]?: boolean;
                } = error.config;
                if (originalRequest[REFRESH_RETRY_FLAG]) {
                    return Promise.reject(error);
                }

                // Check if we have tokens in localStorage
                const currentRefreshToken = getRefreshToken();
                const hasTokens = isAuthenticated();

                // If no tokens, just reject - user isn't logged in
                if (!hasTokens || !currentRefreshToken) {
                    return Promise.reject(error);
                }

                if (!isRefreshing) {
                    isRefreshing = true;
                    try {
                        let newTokens: { accessToken: string; refreshToken: string };

                        // Try Firebase client SDK first (if session exists)
                        if (auth.currentUser) {
                            const accessToken = await auth.currentUser.getIdToken(true);
                            newTokens = {
                                accessToken,
                                refreshToken: auth.currentUser.refreshToken
                            };
                        } else {
                            // No Firebase session - use REST API
                            const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
                            if (!apiKey) {
                                throw new Error("Firebase API key not configured");
                            }
                            newTokens = await refreshTokenViaRestApi(currentRefreshToken, apiKey);
                        }

                        setTokens(newTokens);

                        // Retry queued requests — mark each so a still-401
                        // response cannot kick off another refresh cycle.
                        retryQueueList.forEach(({ config, resolve, reject }) => {
                            (config as any)[REFRESH_RETRY_FLAG] = true;
                            APIHandler.request(config)
                                .then(response => resolve(response))
                                .catch(err => reject(err));
                        });
                        retryQueueList.length = 0;

                        // Retry original request
                        originalRequest[REFRESH_RETRY_FLAG] = true;
                        return APIHandler(originalRequest);
                    } catch (refreshError) {
                        console.error("[useFirebaseToken] Token refresh failed:", refreshError);
                        await clearToken();
                        // Reject all queued requests
                        retryQueueList.forEach(({ reject }) => reject(refreshError));
                        retryQueueList.length = 0;
                        throw refreshError;
                    } finally {
                        isRefreshing = false;
                    }
                }

                // Another refresh is in progress - queue this request
                return new Promise<void>((resolve, reject) => {
                    retryQueueList.push({
                        config: originalRequest,
                        resolve,
                        reject
                    });
                });
            }
        );

        interceptorIdRef.current = interceptorId;

        // Cleanup: eject interceptor when component unmounts or deps change
        return () => {
            if (interceptorIdRef.current !== null) {
                APIHandler.interceptors.response.eject(interceptorIdRef.current);
                interceptorIdRef.current = null;
            }
        };
    }, [auth, isAuthenticated, getAccessToken, getRefreshToken, clearToken, setTokens]);

    return {
        currentAccessToken,
        isAuthenticated,
        isAuthenticatedState,
        setAccessToken,
        setTokens,
        clearToken,
        updateCurrentAccessToken
    };
}
